/*
 * Product reviews widget.
 * Hydrates the rating distribution, review list and submit form from the
 * Railway backend through the storefront App Proxy (/apps/avatar/reviews).
 * The summary stars are already server-rendered, so this only enhances.
 */
(function () {
  'use strict';

  var root = document.querySelector('[data-product-reviews]');
  if (!root) return;

  var PROXY = root.dataset.proxyBase || '/apps/avatar';
  var PRODUCT_ID = root.dataset.productId;
  var PER_PAGE = parseInt(root.dataset.perPage, 10) || 5;
  var LOGGED_IN = root.dataset.loggedIn === 'true';
  var ALLOW_PHOTOS = root.dataset.allowPhotos === 'true';
  var SERVER_RATING_COUNT = Math.max(0, parseInt(root.dataset.ratingCount, 10) || 0);
  var NO_REVIEWS = root.dataset.ratingCount === '0';
  var QUESTIONS_ENABLED = root.dataset.enableQuestions === 'true';
  var QUESTION_LOCALE = root.dataset.questionLocale || document.documentElement.lang || 'en';
  var MAX_PHOTOS = 5;
  var MAX_IMAGE_BYTES = 5 * 1024 * 1024;
  var MAX_VIDEO_BYTES = 50 * 1024 * 1024;

  function isVideoUrl(url) { return /\.(mp4|webm|mov)(\?|$)/i.test(String(url)); }
  function isVideoFile(file) { return String(file.type).indexOf('video/') === 0; }

  var els = {
    distribution: root.querySelector('[data-reviews-distribution]'),
    toolbar: root.querySelector('[data-reviews-toolbar]'),
    filters: root.querySelector('[data-reviews-filters]'),
    sort: root.querySelector('[data-reviews-sort]'),
    list: root.querySelector('[data-reviews-list]'),
    loading: root.querySelector('[data-reviews-loading]'),
    empty: root.querySelector('[data-reviews-empty]'),
    error: root.querySelector('[data-reviews-error]'),
    more: root.querySelector('[data-reviews-more]'),
    write: root.querySelector('[data-reviews-write]'),
    formWrap: root.querySelector('[data-reviews-form-wrap]'),
    form: root.querySelector('[data-reviews-form]'),
    formMsg: root.querySelector('[data-reviews-form-msg]'),
    cancel: root.querySelector('[data-reviews-cancel]'),
    previews: root.querySelector('[data-reviews-previews]'),
    photos: root.querySelector('[data-reviews-photos]'),
    ratingField: root.querySelector('[data-rating-field]'),
    starBtns: Array.prototype.slice.call(root.querySelectorAll('[data-star-value]')),
    questionRoot: root.querySelector('[data-product-questions]'),
    questionList: root.querySelector('[data-question-list]'),
    questionLoading: root.querySelector('[data-question-loading]'),
    questionEmpty: root.querySelector('[data-question-empty]'),
    questionError: root.querySelector('[data-question-error]'),
    questionFormWrap: root.querySelector('[data-question-form-wrap]'),
    questionForm: root.querySelector('[data-question-form]'),
    questionFormMessage: root.querySelector('[data-question-form-message]'),
    questionOpen: root.querySelector('[data-question-open]'),
    questionClose: root.querySelector('[data-question-close]'),
    questionCancel: root.querySelector('[data-question-cancel]')
  };

  var state = { page: 1, sort: 'recent', star: 0, photosOnly: false, loading: false, loaded: false, files: [] };

  // Demo mode: append ?reviews_demo=1 to the product URL to preview the full UI
  // with mock data (no Railway backend needed). Has no effect in production URLs.
  var DEMO = /[?&]reviews_demo=1\b/.test(window.location.search);

  var I18N = {};
  try {
    // The JSON payload is a sibling of the section so it remains outside the
    // visual landmark. Read it from the document, not from `root`.
    var i18nEl = document.querySelector('[data-reviews-i18n]');
    if (i18nEl) I18N = JSON.parse(i18nEl.textContent);
  } catch (e) { I18N = {}; }

  function t(key, fallback) {
    // Liquid translations can already contain HTML entities before | json.
    // Normalize one layer without parsing markup; text sinks stay textContent
    // and HTML templates escape the result at their own output boundary.
    var entities = { '&amp;': '&', '&lt;': '<', '&gt;': '>', '&quot;': '"', '&apos;': "'", '&#39;': "'", '&#x27;': "'" };
    return String(I18N[key] || fallback).replace(/&(?:amp|lt|gt|quot|apos|#39|#x27);/gi, function (entity) {
      return entities[entity.toLowerCase()];
    });
  }

  function requestJson(url, options) {
    var controller = typeof AbortController === 'function' ? new AbortController() : null;
    var timer = controller ? setTimeout(function () { controller.abort(); }, 8000) : null;
    var requestOptions = Object.assign({}, options || {});
    if (controller) requestOptions.signal = controller.signal;
    return fetch(url, requestOptions).then(function (response) {
      if (!response.ok) throw new Error('http');
      return response.json();
    }).then(function (payload) {
      if (timer) clearTimeout(timer);
      return payload;
    }, function (error) {
      if (timer) clearTimeout(timer);
      throw error;
    });
  }

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function starMarkup(rating) {
    var floor = Math.floor(rating);
    var dec = rating - floor;
    var ratingDecimal = 0;
    if (dec >= 0.3 && dec <= 0.7) ratingDecimal = 0.5;
    else if (dec > 0.7) ratingDecimal = 1;
    return (
      '<span class="rating" role="img"><span class="rating-star" style="--rating:' +
      floor + ';--rating-max:5;--rating-decimal:' + ratingDecimal + ';"></span></span>'
    );
  }

  // ---- Product Q&A --------------------------------------------------------

  function renderQuestion(item) {
    var article = document.createElement('article');
    article.className = 'product-questions__item';
    article.setAttribute('role', 'listitem');
    article.innerHTML =
      '<div class="product-questions__question-mark" aria-hidden="true">Q</div>' +
      '<div class="product-questions__item-copy">' +
        '<h3 class="product-questions__question">' + esc(item.question) + '</h3>' +
        '<div class="product-questions__answer">' +
          '<span class="product-questions__answer-badge">' + esc(t('qaAnswerLabel', 'alAsil answer')) + '</span>' +
          '<p>' + esc(item.answer) + '</p>' +
        '</div>' +
        (item.date ? '<time class="product-questions__date">' + esc(item.date) + '</time>' : '') +
      '</div>';
    return article;
  }

  function loadQuestions() {
    if (!QUESTIONS_ENABLED || !els.questionRoot) return;
    show(els.questionLoading);
    hide(els.questionEmpty);
    hide(els.questionError);
    requestJson(PROXY + '/questions?product_id=' + encodeURIComponent(PRODUCT_ID) + '&limit=8', {
      headers: { Accept: 'application/json' }
    }).then(function (payload) {
      hide(els.questionLoading);
      var items = Array.isArray(payload.questions) ? payload.questions : [];
      els.questionList.innerHTML = '';
      items.forEach(function (item) { els.questionList.appendChild(renderQuestion(item)); });
      toggle(els.questionEmpty, items.length === 0);
    }).catch(function () {
      hide(els.questionLoading);
      show(els.questionError);
    });
  }

  function currentQuestionProductId() {
    // Linked product navigation replaces MainProduct, while this reviews
    // section survives. Questions belong to the product currently shown.
    var main = document.querySelector('[data-analytics-product-id][id^="MainProduct-"]');
    var productId = main && main.dataset.analyticsProductId;
    return /^\d+$/.test(productId || '') ? productId : PRODUCT_ID;
  }

  function openQuestionForm() {
    if (!els.questionFormWrap) return;
    var wasHidden = els.questionFormWrap.hidden;
    els.questionFormWrap.hidden = false;
    if (wasHidden && typeof window.publishAlasilEvent === 'function') {
      window.publishAlasilEvent('alasil:storefront_interaction', { event_name: 'product_question_opened', product_id: currentQuestionProductId() });
    }
    els.questionFormWrap.scrollIntoView({ behavior: 'smooth', block: 'center' });
    var field = els.questionForm && els.questionForm.querySelector('textarea[name="question"]');
    if (field) field.focus({ preventScroll: true });
  }

  function closeQuestionForm() {
    if (els.questionFormWrap) els.questionFormWrap.hidden = true;
  }

  function questionMessage(message, ok) {
    if (!els.questionFormMessage) return;
    els.questionFormMessage.hidden = false;
    els.questionFormMessage.textContent = message;
    els.questionFormMessage.className = 'product-questions__form-message ' + (ok ? 'is-ok' : 'is-error');
  }

  if (els.questionOpen) els.questionOpen.addEventListener('click', openQuestionForm);
  if (els.questionClose) els.questionClose.addEventListener('click', closeQuestionForm);
  if (els.questionCancel) els.questionCancel.addEventListener('click', closeQuestionForm);
  if (els.questionForm) {
    els.questionForm.addEventListener('submit', function (event) {
      event.preventDefault();
      var data = new FormData(els.questionForm);
      var question = String(data.get('question') || '').trim();
      if (question.length < 8) {
        questionMessage(t('qaErrShort', 'Please write a clear question of at least 8 characters.'), false);
        return;
      }
      var submit = els.questionForm.querySelector('[data-question-submit]');
      var submittedProductId = currentQuestionProductId();
      var submittedItem = typeof window.AlAsilProductAnalyticsItem === 'function' ? window.AlAsilProductAnalyticsItem(submittedProductId) : null;
      if (submit) submit.disabled = true;
      questionMessage(t('qaSubmitting', 'Submitting…'), true);
      fetch(PROXY + '/questions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          product_id: submittedProductId,
          question: question,
          author_display_name: data.get('author_display_name') || '',
          email: data.get('email') || '',
          locale: QUESTION_LOCALE
        })
      }).then(function (response) {
        return response.json().then(function (payload) { return { ok: response.ok, payload: payload }; });
      }).then(function (result) {
        if (!result.ok) throw new Error(result.payload && result.payload.error || 'submit');
        questionMessage(t('qaThanks', 'Thanks! We received your question and will publish it with the answer after review.'), true);
        if (typeof window.publishAlasilEvent === 'function') {
          var interaction = { event_name: 'product_question_submitted', product_id: submittedProductId };
          if (submittedItem) interaction.items = [submittedItem];
          window.publishAlasilEvent('alasil:storefront_interaction', interaction);
        }
        els.questionForm.reset();
      }).catch(function () {
        questionMessage(t('qaErrGeneric', "We couldn't submit your question. Please try again."), false);
      }).then(function () {
        if (submit) submit.disabled = false;
      });
    });
  }

  // ---- Store review fallback ----------------------------------------------

  // Google store reviews are rendered by Liquid; this script only enhances
  // product reviews and questions, keeping store ratings out of Product schema.
  // ---- Fetch + render list -------------------------------------------------

  function buildQuery() {
    var q = '?product_id=' + encodeURIComponent(PRODUCT_ID) +
      '&page=' + state.page + '&per_page=' + PER_PAGE + '&sort=' + state.sort;
    if (state.star) q += '&star=' + state.star;
    if (state.photosOnly) q += '&photos=1';
    return q;
  }

  function load(reset) {
    if (state.loading) return;
    if (reset) { state.page = 1; els.list.innerHTML = ''; }
    state.loading = true;
    show(els.loading);
    hide(els.empty); hide(els.error);

    if (DEMO) { loadDemo(reset); return; }

    requestJson(PROXY + '/reviews' + buildQuery(), { headers: { Accept: 'application/json' } })
      .then(function (data) {
        hide(els.loading);
        state.loaded = true;
        var apiCount = Math.max(0, Number(data.count) || 0);
        if (els.distribution) els.distribution.hidden = apiCount === 0;
        if (data.distribution && apiCount > 0) renderDistribution(data.distribution, apiCount);
        var reviews = data.reviews || [];
        if (reset && reviews.length === 0) {
          if (SERVER_RATING_COUNT > 0 && els.empty) {
            els.empty.textContent = t('detailsUnavailable', 'Review details are temporarily unavailable.');
          }
          show(els.empty);
        }
        reviews.forEach(function (rv) { els.list.appendChild(renderReview(rv)); });
        if (els.toolbar && (data.count || reviews.length)) els.toolbar.hidden = false;
        toggle(els.more, !!data.has_more);
        if (reset) injectReviewJsonLd(reviews);
      })
      .catch(function () {
        hide(els.loading);
        if (state.page === 1) show(els.error);
      })
      .then(function () { state.loading = false; });
  }

  // ---- Demo data (only when ?reviews_demo=1) -------------------------------

  var DEMO_REVIEWS = [
    {
      id: 'd1', rating: 5, author: 'Sara M.', verified: true, date: '2 weeks ago',
      title: 'Exactly what I hoped for',
      body: 'Build quality is excellent and it arrived a day early. The finish feels premium and it has worked flawlessly so far. Highly recommend.',
      media: [
        'https://images.unsplash.com/photo-1505740420928-5e560c06d30e?w=200&h=200&fit=crop',
        'https://images.unsplash.com/photo-1484704849700-f032a568e944?w=200&h=200&fit=crop'
      ],
      merchant_reply: 'Thank you for the kind words, Sara! Enjoy your purchase.',
      helpful_count: 12
    },
    {
      id: 'd2', rating: 4, author: 'Ahmed K.', verified: true, date: '3 weeks ago',
      title: 'Great value',
      body: 'Does everything it promises. Took one star off because the cable is a little short, but overall very happy.',
      media: [], merchant_reply: '', helpful_count: 5
    },
    {
      id: 'd3', rating: 5, author: 'Lina', verified: false, date: '1 month ago',
      title: '', body: 'Beautiful design and super easy to set up. Would buy again!',
      media: ['https://images.unsplash.com/photo-1572569511254-d8f925fe2cbb?w=200&h=200&fit=crop'],
      merchant_reply: '', helpful_count: 3
    },
    {
      id: 'd4', rating: 3, author: 'Omar F.', verified: true, date: '1 month ago',
      title: 'It is okay', body: 'Works as described but nothing special. The packaging could be better protected during shipping.',
      media: [], merchant_reply: 'Sorry to hear about the packaging - we are improving it. Reach out and we will make it right.',
      helpful_count: 1
    },
    {
      id: 'd5', rating: 2, author: 'Guest', verified: false, date: '2 months ago',
      title: 'Not for me', body: 'Smaller than I expected from the photos. Quality is fine but check the dimensions before ordering.',
      media: [], merchant_reply: '', helpful_count: 0
    },
    {
      id: 'd6', rating: 5, author: 'Fatima A.', verified: true, date: '2 months ago',
      title: 'Love it!', body: 'My second purchase from this store. Consistently great products and fast delivery.',
      media: ['https://images.unsplash.com/photo-1523275335684-37898b6baf30?w=200&h=200&fit=crop'],
      merchant_reply: '', helpful_count: 8
    },
    {
      id: 'd7', rating: 4, author: 'Yousef', verified: false, date: '3 months ago',
      title: '', body: 'Solid product for the price. Battery life is good and the app is intuitive.',
      media: [], merchant_reply: '', helpful_count: 2
    }
  ];

  var DEMO_DIST = { 5: 3, 4: 2, 3: 1, 2: 1, 1: 0 };
  var DEMO_COUNT = DEMO_REVIEWS.length;

  function demoSummary() {
    var sum = 0, n = 0, i;
    for (i = 1; i <= 5; i++) { sum += i * (DEMO_DIST[i] || 0); n += DEMO_DIST[i] || 0; }
    var avg = n ? sum / n : 0;
    var avgEl = root.querySelector('.product-reviews__avg');
    if (avgEl) avgEl.textContent = (Math.round(avg * 10) / 10).toFixed(1);
    var countEl = root.querySelector('.product-reviews__count');
    if (countEl) countEl.textContent = 'Based on ' + n + ' reviews';
    var floor = Math.floor(avg), dec = avg - floor, rd = 0;
    if (dec >= 0.3 && dec <= 0.7) rd = 0.5; else if (dec > 0.7) rd = 1;
    var starEl = root.querySelector('.product-reviews__summary .rating-star');
    if (starEl) {
      starEl.style.setProperty('--rating', floor);
      starEl.style.setProperty('--rating-decimal', rd);
    }

    // Also fill the compact rating summaries near the product title (they are
    // server-rendered from the metafield, so demo mode has to patch them in JS).
    // There can be two: the mobile-header one and the desktop info-column one.
    var summaryHtml =
      '<span class="rating" role="img" aria-hidden="true">' +
        '<span class="rating-star" style="--rating:' + floor + ';--rating-max:5;--rating-decimal:' + rd + ';"></span>' +
      '</span>' +
      '<span class="product-rating-summary__value" aria-hidden="true">' + (Math.round(avg * 10) / 10).toFixed(1) + '</span>' +
      '<span class="product-rating-summary__count" aria-hidden="true">(' + n + ')</span>';
    document.querySelectorAll('.product-rating-summary').forEach(function (summary) {
      summary.classList.remove('product-rating-summary--empty');
      summary.innerHTML = summaryHtml;
    });
  }

  function loadDemo(reset) {
    var filtered = DEMO_REVIEWS.slice();
    if (state.star) filtered = filtered.filter(function (r) { return r.rating === state.star; });
    if (state.photosOnly) filtered = filtered.filter(function (r) { return r.media && r.media.length; });

    if (state.sort === 'highest') filtered.sort(function (a, b) { return b.rating - a.rating; });
    else if (state.sort === 'lowest') filtered.sort(function (a, b) { return a.rating - b.rating; });
    else if (state.sort === 'helpful') filtered.sort(function (a, b) { return b.helpful_count - a.helpful_count; });

    var start = (state.page - 1) * PER_PAGE;
    var pageItems = filtered.slice(start, start + PER_PAGE);
    var hasMore = start + PER_PAGE < filtered.length;

    setTimeout(function () {
      hide(els.loading);
      state.loaded = true;
      demoSummary();
      renderDistribution(DEMO_DIST, DEMO_COUNT);
      if (reset && filtered.length === 0) show(els.empty);
      pageItems.forEach(function (rv) { els.list.appendChild(renderReview(rv)); });
      if (els.toolbar) els.toolbar.hidden = false;
      toggle(els.more, hasMore);
      state.loading = false;
    }, 250);
  }

  function renderDistribution(dist, total) {
    if (!els.distribution || !total) return;
    for (var i = 1; i <= 5; i++) {
      var row = els.distribution.querySelector('[data-dist-row="' + i + '"]');
      if (!row) continue;
      var n = dist[i] || 0;
      var pct = total ? Math.round((n / total) * 100) : 0;
      row.querySelector('.product-reviews__dist-fill').style.setProperty('--pct', pct + '%');
      row.querySelector('[data-dist-count]').textContent = n;
    }
  }

  function renderReview(rv) {
    var item = document.createElement('article');
    item.className = 'product-reviews__item';
    item.setAttribute('role', 'listitem');

    var verified = rv.verified
      ? '<span class="product-reviews__verified" title="' + esc(t('verifiedBuyer', 'Verified buyer')) + '">✓ ' +
        esc(t('verifiedBuyer', 'Verified buyer')) + '</span>'
      : '';

    var media = '';
    if (rv.media && rv.media.length) {
      media = '<div class="product-reviews__item-media">' + rv.media.map(function (url) {
        if (isVideoUrl(url)) {
          return '<video class="product-reviews__item-video" src="' + esc(url) +
            '" controls preload="metadata" playsinline width="120" height="120"></video>';
        }
        return '<a href="' + esc(url) + '" target="_blank" rel="noopener"><img src="' + esc(url) +
          '" alt="" loading="lazy" width="72" height="72"></a>';
      }).join('') + '</div>';
    }

    var reply = '';
    if (rv.merchant_reply) {
      reply = '<div class="product-reviews__reply"><strong>' + esc(t('storeReply', 'Store reply')) +
        '</strong><p>' + esc(rv.merchant_reply) + '</p></div>';
    }

    item.innerHTML =
      '<header class="product-reviews__item-head">' +
        starMarkup(rv.rating) +
        '<span class="product-reviews__item-author">' + esc(rv.author || t('anonymous', 'Anonymous')) + '</span>' +
        verified +
        '<time class="product-reviews__item-date">' + esc(rv.date || '') + '</time>' +
      '</header>' +
      (rv.title ? '<h4 class="product-reviews__item-title">' + esc(rv.title) + '</h4>' : '') +
      '<p class="product-reviews__item-body">' + esc(rv.body || '') + '</p>' +
      media +
      reply +
      '<button type="button" class="product-reviews__helpful" data-helpful-id="' + esc(rv.id) + '">' +
        '👍 ' + esc(t('helpful', 'Helpful')) + ' <span>(' + (rv.helpful_count || 0) + ')</span>' +
      '</button>';
    return item;
  }

  // ---- Review JSON-LD (Google rich results) --------------------------------

  // The Product node already carries aggregateRating (server-rendered from the
  // reviews.rating metafield). Here we enrich that SAME node with individual
  // `review` entries so Google can show review snippets - injecting into the
  // existing script avoids emitting a second, conflicting Product node.
  var jsonLdDone = false;
  function injectReviewJsonLd(reviews) {
    if (jsonLdDone || DEMO || !reviews || !reviews.length) return;
    var script = document.getElementById('product-schema-jsonld');
    if (!script) return;
    var data;
    try { data = JSON.parse(script.textContent); } catch (e) { return; }
    if (!data || data['@type'] !== 'Product') return;

    data.review = reviews.slice(0, 10).map(function (rv) {
      var review = {
        '@type': 'Review',
        reviewRating: {
          '@type': 'Rating',
          ratingValue: rv.rating,
          bestRating: 5,
          worstRating: 1
        },
        author: { '@type': 'Person', name: rv.author || t('anonymous', 'Anonymous') }
      };
      if (rv.title) review.name = rv.title;
      if (rv.body) review.reviewBody = rv.body;
      if (rv.submitted_at) review.datePublished = String(rv.submitted_at).slice(0, 10);
      return review;
    });

    try { script.textContent = JSON.stringify(data); } catch (e) { return; }
    jsonLdDone = true;
  }

  // ---- Helpers -------------------------------------------------------------

  function show(el) { if (el) el.hidden = false; }
  function hide(el) { if (el) el.hidden = true; }
  function toggle(el, on) { if (el) el.hidden = !on; }

  // ---- Events --------------------------------------------------------------

  if (els.sort) {
    els.sort.addEventListener('change', function () { state.sort = els.sort.value; load(true); });
  }

  if (els.filters) {
    els.filters.addEventListener('click', function (e) {
      var starBtn = e.target.closest('[data-filter-star]');
      var photoBtn = e.target.closest('[data-filter-photos]');
      if (starBtn) {
        state.star = parseInt(starBtn.dataset.filterStar, 10) || 0;
        els.filters.querySelectorAll('[data-filter-star]').forEach(function (b) {
          b.classList.toggle('is-active', b === starBtn);
        });
        load(true);
      } else if (photoBtn) {
        state.photosOnly = !state.photosOnly;
        photoBtn.classList.toggle('is-active', state.photosOnly);
        load(true);
      }
    });
  }

  if (els.more) {
    els.more.addEventListener('click', function () { state.page += 1; load(false); });
  }

  els.list.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-helpful-id]');
    if (!btn || btn.disabled) return;
    btn.disabled = true;
    if (DEMO) {
      var span = btn.querySelector('span');
      if (span) {
        var n = parseInt((span.textContent || '').replace(/\D/g, ''), 10) || 0;
        span.textContent = '(' + (n + 1) + ')';
      }
      return;
    }
    fetch(PROXY + '/reviews/helpful', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: btn.dataset.helpfulId })
    }).then(function (r) { return r.ok ? r.json() : null; }).then(function (d) {
      if (d && typeof d.helpful_count === 'number') {
        var span = btn.querySelector('span');
        if (span) span.textContent = '(' + d.helpful_count + ')';
      }
    }).catch(function () { btn.disabled = false; });
  });

  // ---- Form ----------------------------------------------------------------

  function openForm() {
    if (!els.formWrap) return;
    els.formWrap.hidden = false;
    els.formWrap.scrollIntoView({ behavior: 'smooth', block: 'center' });
    var selectedStar = els.starBtns.find(function (button) {
      return button.getAttribute('aria-checked') === 'true';
    });
    if (selectedStar) selectedStar.focus();
  }
  function closeForm() { if (els.formWrap) els.formWrap.hidden = true; }

  if (els.write) els.write.addEventListener('click', openForm);
  if (els.cancel) els.cancel.addEventListener('click', closeForm);

  function setRating(val) {
    if (els.ratingField) els.ratingField.value = val;
    els.starBtns.forEach(function (b) {
      var on = parseInt(b.dataset.starValue, 10) <= val;
      b.classList.toggle('is-on', on);
      b.setAttribute('aria-checked', parseInt(b.dataset.starValue, 10) === val ? 'true' : 'false');
    });
  }
  els.starBtns.forEach(function (b) {
    b.addEventListener('click', function () { setRating(parseInt(b.dataset.starValue, 10)); });
  });
  setRating(5);

  if (ALLOW_PHOTOS && els.photos) {
    els.photos.addEventListener('change', function () {
      state.files = [];
      if (els.previews) els.previews.innerHTML = '';
      var files = Array.prototype.slice.call(els.photos.files).slice(0, MAX_PHOTOS);
      files.forEach(function (f) {
        var vid = isVideoFile(f);
        if (f.size > (vid ? MAX_VIDEO_BYTES : MAX_IMAGE_BYTES)) return;
        state.files.push(f);
        if (els.previews) {
          if (vid) {
            var v = document.createElement('video');
            v.className = 'product-reviews__preview';
            v.src = URL.createObjectURL(f);
            v.muted = true;
            v.setAttribute('playsinline', '');
            els.previews.appendChild(v);
          } else {
            var img = document.createElement('img');
            img.className = 'product-reviews__preview';
            img.alt = '';
            img.src = URL.createObjectURL(f);
            els.previews.appendChild(img);
          }
        }
      });
    });
  }

  function formMessage(text, ok) {
    if (!els.formMsg) return;
    els.formMsg.hidden = false;
    els.formMsg.textContent = text;
    els.formMsg.className = 'product-reviews__form-msg ' + (ok ? 'is-ok' : 'is-error');
  }

  if (els.form) {
    els.form.addEventListener('submit', function (e) {
      e.preventDefault();
      var fd = new FormData(els.form);
      if (!fd.get('rating')) { formMessage(t('errRating', 'Please select a rating.'), false); return; }
      if (!String(fd.get('body') || '').trim()) { formMessage(t('errBody', 'Please write your review.'), false); return; }

      var submitBtn = els.form.querySelector('[data-reviews-submit]');
      if (submitBtn) submitBtn.disabled = true;
      formMessage(t('submitting', 'Submitting…'), true);

      var payload = {
        product_id: PRODUCT_ID,
        rating: Number(fd.get('rating')),
        title: fd.get('title') || '',
        body: fd.get('body') || '',
        author_display_name: fd.get('author_display_name') || '',
        email: fd.get('email') || ''
      };

      uploadPhotos(state.files).then(function (urls) {
        payload.media_urls = urls;
        return fetch(PROXY + '/reviews', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(payload)
        });
      }).then(function (r) {
        return r.json().then(function (d) { return { ok: r.ok, data: d }; });
      }).then(function (res) {
        if (submitBtn) submitBtn.disabled = false;
        if (!res.ok) throw new Error('submit');
        if (res.data && res.data.published) {
          formMessage(t('thanksPublished', 'Thanks! Your review is now live.'), true);
          load(true);
        } else {
          formMessage(t('thanksPending', 'Thanks! Your review will appear after approval.'), true);
        }
        els.form.reset();
        setRating(5);
        state.files = [];
        if (els.previews) els.previews.innerHTML = '';
      }).catch(function () {
        if (submitBtn) submitBtn.disabled = false;
        formMessage(t('errGeneric', 'Something went wrong. Please try again.'), false);
      });
    });
  }

  // Presign each photo via the proxy, PUT to storage, return public URLs.
  function uploadPhotos(files) {
    if (!ALLOW_PHOTOS || !files.length) return Promise.resolve([]);
    return Promise.all(files.map(function (file) {
      return fetch(PROXY + '/reviews/sign', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ contentType: file.type, size: file.size })
      }).then(function (r) { if (!r.ok) throw new Error('sign'); return r.json(); })
        .then(function (s) {
          return fetch(s.uploadUrl, { method: 'PUT', headers: s.headers, body: file })
            .then(function (p) { if (!p.ok) throw new Error('put'); return s.publicUrl; });
        });
    }));
  }

  // ---- Lazy init when scrolled into view -----------------------------------

  // In demo mode, patch the title summary eagerly - it sits above the fold so
  // the lazy list loader (below) would not reach it until the user scrolls.
  if (DEMO) demoSummary();

  loadQuestions();

  // No reviews yet: the server already rendered the empty-state hero, so skip
  // the network fetch + spinner entirely. The "Write a review" form is still
  // wired up above, so visitors can submit the first review.
  if (NO_REVIEWS && !DEMO) return;

  // The section loader already gates this script by proximity. Do not add a
  // second near-viewport wait for known product reviews.
  if (SERVER_RATING_COUNT > 0 && !DEMO) { load(true); return; }

  if ('IntersectionObserver' in window) {
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting && !state.loaded) { load(true); io.disconnect(); }
      });
    }, { rootMargin: '200px' });
    io.observe(root);
  } else {
    load(true);
  }
})();
